// src/pages/AccountPage.js

import React, { useMemo, useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Typography,
  Paper,
  Grid,
  Divider,
  Button,
  CircularProgress
} from '@mui/material';
import InitialSetupPage from '../components/InitialSetupPage';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../firebase'; 
import { getFunctions, httpsCallable } from 'firebase/functions';
import { toast } from 'react-toastify';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CableIcon from '@mui/icons-material/Cable';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { loadStripe } from '@stripe/stripe-js';
import { v4 as uuidv4 } from 'uuid';
import stripeConfig from '../config/stripe';

// NEW IMPORT
import TwoFactorAuthCard from '../components/TwoFactorAuthCard';

// NEW: Initialize Stripe outside of the component render.
const stripePromise = loadStripe(stripeConfig.publishableKey);
// A small helper component to display each piece of data clearly
const InfoRow = ({ label, value, isTotal = false }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5 }}>
    <Typography variant="body1" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body1" sx={{ fontWeight: isTotal ? 'bold' : 'normal' }}>
      {value}
    </Typography>
  </Box>
);

function AccountPage({ user, userData, clients = [], onClientDataChange, asanaConnection, isAsanaLoading, onOpenWizard }) {
// NEW: State to manage the setup completion success message
const [completedClientName, setCompletedClientName] = useState(null);
// NEW: State for client creation modal
const [isCreating, setIsCreating] = useState(false);
const [isRedirecting, setIsRedirecting] = useState(false);
const [isPortalLoading, setIsPortalLoading] = useState(false);
const [isActivating, setIsActivating] = useState(false);
const [isConnectingAsana, setIsConnectingAsana] = useState(false);
const [isDisconnectingAsana, setIsDisconnectingAsana] = useState(false);
const [asanaAuthUrl, setAsanaAuthUrl] = useState(null);

useEffect(() => {
  const handleCheckoutSuccess = async (sessionId) => {
    const toastId = toast.loading("Finalizing your subscription setup...");
    try {
      const functions = getFunctions();
      const handleSuccessfulCheckout = httpsCallable(functions, 'handleSuccessfulCheckout');
      const result = await handleSuccessfulCheckout({ sessionId });

      if (result.data.success) {
        toast.update(toastId, { render: "Subscription activated successfully!", type: "success", isLoading: false, autoClose: 5000 });
        // Trigger the parent to refresh all user data
        if (onClientDataChange) {
          onClientDataChange();
        }
      } else {
        throw new Error("Failed to sync payment details.");
      }
    } catch (error) {
      console.error("Error finalizing checkout:", error);
      toast.update(toastId, { render: `Error: ${error.message}`, type: "error", isLoading: false, autoClose: 5000 });
    } finally {
      // Clean the URL regardless of outcome
      window.history.replaceState(null, null, window.location.pathname);
    }
  };

  const query = new URLSearchParams(window.location.search);
  const sessionId = query.get('session_id');

  if (query.get('checkout_success') && sessionId) {
    handleCheckoutSuccess(sessionId);
  } else if (query.get('checkout_canceled')) {
    toast.info("Adding a payment method was canceled.");
    window.history.replaceState(null, null, window.location.pathname);
  } else if (query.get('asana_connected')) {
    toast.success("Asana connected successfully!");
    window.history.replaceState(null, null, window.location.pathname);
  }
}, [onClientDataChange]);

  // Memoize calculations to prevent re-running on every render
  const subscriptionDetails = useMemo(() => {
    const validClients = Array.isArray(clients) ? clients : [];
    const activeClientsCount = validClients.filter(c => c.isActive === true).length;
    const costPerClient = userData?.costPerClient ?? 0;
    // Check if the subscription is actually active, not just if it exists.
    const hasActiveSubscription = userData?.stripeSubscriptionId && userData?.stripeSubscriptionStatus === 'active';
    const billableClients = activeClientsCount; // no forced minimum
    const totalMonthlyCost = hasActiveSubscription ? (billableClients * costPerClient) : 0;
    return {
      activeClientsCount,
      costPerClient,
      totalMonthlyCost,
};
}, [userData, clients]);

// Callback for when a client setup is successfully completed
const handleClientSetupComplete = (clientName) => {
        setCompletedClientName(clientName);
        // Refresh client data (but don't trigger the generic toast)
        if (onClientDataChange) {
            onClientDataChange({ silent: true });
        }
    };

    const handlePasswordReset = async () => {
        if (!user || !user.email) {
      toast.error("Could not find user email. Please try again.");
      return;
    }

    try {
      // Dynamically get the auth domain from environment variables
const authDomain = process.env.REACT_APP_FIREBASE_AUTH_DOMAIN;

const actionCodeSettings = {
    url: `https://${authDomain.replace('.firebaseapp.com', '.web.app')}/reset-password`,
    handleCodeInApp: true,
};

      await sendPasswordResetEmail(auth, user.email, actionCodeSettings);
      toast.success(`A password reset link has been sent to ${user.email}. Please check your inbox.`);
    } catch (error) {
      console.error("Error sending password reset email:", error);
      toast.error("Failed to send password reset email. Please try again later.");
    }
  };

  // Correctly handles creation of subsequent clients (after the first one)
    const handleCreateClient = async () => {
    setIsCreating(true);
    try {
        const functions = getFunctions();
        const createBlankClient = httpsCallable(functions, 'createBlankClient');
        
        await createBlankClient();
        toast.info('New client created. Please complete the setup process.');
        
        if (onClientDataChange) {
            onClientDataChange();
        }
    } catch (error) {
        console.error('Error creating new client:', error);
        toast.error(error.message || 'Failed to create new client. Please try again.');
    } finally {
        setIsCreating(false);
    }
};

  // NEW: Function to handle redirecting to Stripe Checkout
const handleRedirectToCheckout = async () => {
    setIsRedirecting(true);
    try {
        const functions = getFunctions();
        const createCheckoutSession = httpsCallable(functions, 'createCheckoutSession');
        const idempotencyKey = uuidv4();
        const result = await createCheckoutSession({ idempotencyKey });

        const { sessionId } = result.data;
        if (!sessionId) {
            throw new Error("Could not retrieve a checkout session ID.");
        }

        const stripe = await stripePromise;
        const { error } = await stripe.redirectToCheckout({ sessionId });

        if (error) {
            console.error("Stripe redirect error:", error);
            toast.error(error.message || "Failed to redirect to payment page.");
        }
    } catch (error) {
        console.error("Error preparing for checkout:", error);
        toast.error(error.message || "An error occurred. Please try again.");
    } finally {
        setIsRedirecting(false);
    }
};

  const handleConnectToAsana = async () => {
  setIsConnectingAsana(true);
  const toastId = toast.loading("Generating secure connection link...");
  try {
    const functions = getFunctions(undefined, 'us-central1'); // Use region for reliability
    const initiateAsanaAuth = httpsCallable(functions, 'initiateAsanaAuth');
    const result = await initiateAsanaAuth();
    if (result.data.authUrl) {
  toast.update(toastId, { render: "Link generated. Please proceed.", type: "success", isLoading: false, autoClose: 3000 });
  setAsanaAuthUrl(result.data.authUrl); // Set the URL in state
} else {
      throw new Error("Could not retrieve Asana authorization URL.");
    }
  } catch (error) {
    console.error("Error initiating Asana auth:", error);
    toast.update(toastId, { render: `Error: ${error.message}`, type: "error", isLoading: false, autoClose: 5000 });
  } finally {
      setIsConnectingAsana(false); // Turn off spinner after fetching
  }
};

  const handleDisconnectAsana = async () => {
  if (!window.confirm("Are you sure you want to disconnect your Asana account? This will remove the integration's access.")) {
    return;
  }
  setIsDisconnectingAsana(true);
  const toastId = toast.loading("Disconnecting Asana...");
  try {
    const functions = getFunctions();
    const disconnectAsana = httpsCallable(functions, 'disconnectAsana');
    const result = await disconnectAsana();

    if (result.data.success) {
      toast.update(toastId, { render: "Asana disconnected successfully.", type: "success", isLoading: false, autoClose: 5000 });
      // The onSnapshot listener will automatically update the UI
    } else {
      throw new Error(result.data.message || "Failed to disconnect.");
    }
  } catch (error) {
    console.error("Error disconnecting Asana:", error);
    toast.update(toastId, { render: `Error: ${error.message}`, type: "error", isLoading: false, autoClose: 5000 });
  } finally {
    setIsDisconnectingAsana(false);
  }
};

const handleManageBilling = async () => {
    setIsPortalLoading(true);
    const toastId = toast.loading("Redirecting to your billing portal...");
    try {
        const functions = getFunctions();
        const createBillingPortalSession = httpsCallable(functions, 'createBillingPortalSession');
        const result = await createBillingPortalSession();
        
        const { url } = result.data;
        if (url) {
            toast.update(toastId, { render: "Redirecting...", type: "success", isLoading: false, autoClose: 2000 });
            window.location.href = url;
        } else {
            throw new Error("Could not retrieve billing portal URL.");
        }
    } catch (error) {
        console.error("Error creating billing portal session:", error);
        toast.update(toastId, { render: `Error: ${error.message}`, type: "error", isLoading: false, autoClose: 5000 });
    } finally {
        setIsPortalLoading(false);
    }
};

const handleActivateSubscription = async () => {
    if (!window.confirm('Activate your subscription now? You will be charged $95.00 at the end of your trial period, and then monthly thereafter.')) {
        return;
    }
    
    setIsActivating(true);
    const toastId = toast.loading("Activating your subscription...");
    try {
        const functions = getFunctions();
        const activateSubscription = httpsCallable(functions, 'activateSubscription');
        const result = await activateSubscription();
        
        toast.update(toastId, { 
            render: result.data.message || "Subscription activated successfully!", 
            type: "success", 
            isLoading: false, 
            autoClose: 5000 
        });
        
        // Refresh user data to update UI
        if (onClientDataChange) {
            onClientDataChange();
        }
    } catch (error) {
        console.error("Error activating subscription:", error);
        toast.update(toastId, { 
            render: `Error: ${error.message}`, 
            type: "error", 
            isLoading: false, 
            autoClose: 5000 
        });
    } finally {
        setIsActivating(false);
    }
};

  const handleCancelSetup = async (clientId) => {
    if (!window.confirm('Are you sure you want to cancel this client setup? This will delete the client record.')) {
        return;
    }
    
    const toastId = toast.loading('Canceling setup...');
    try {
        const functions = getFunctions();
        const deleteClient = httpsCallable(functions, 'deleteClientAndAssociatedData');
        
        await deleteClient({ clientId });
        toast.update(toastId, { 
            render: 'Setup canceled successfully', 
            type: 'success', 
            isLoading: false, 
            autoClose: 3000 
        });
        
        if (onClientDataChange) {
            onClientDataChange();
        }
    } catch (error) {
        console.error('Error canceling setup:', error);
        toast.update(toastId, { 
            render: `Failed to cancel: ${error.message}`, 
            type: 'error', 
            isLoading: false, 
            autoClose: 5000 
        });
    }
};

const hasPaymentMethod = userData?.stripeSubscriptionStatus === 'active';

// Payment failure detection
const isPaymentPastDue = userData?.stripeSubscriptionStatus === 'past_due';
console.log('🔴 PAYMENT DEBUG:', { 
    stripeSubscriptionStatus: userData?.stripeSubscriptionStatus, 
    isPaymentPastDue,
    userData 
});
const gracePeriodEndsAt = userData?.gracePeriodEndsAt;
const paymentFailureReason = userData?.lastPaymentFailureReason;

// Format grace period end date for display
const gracePeriodEndDate = useMemo(() => {
    if (!gracePeriodEndsAt) return null;
    // Handle Firestore Timestamp or Date object
    const date = gracePeriodEndsAt.toDate ? gracePeriodEndsAt.toDate() : new Date(gracePeriodEndsAt);
    return date.toLocaleDateString('en-US', { 
        month: 'long', 
        day: 'numeric', 
        year: 'numeric' 
    });
}, [gracePeriodEndsAt]);

// Calculate days remaining in grace period
const gracePeriodDaysRemaining = useMemo(() => {
    if (!gracePeriodEndsAt) return null;
    const endDate = gracePeriodEndsAt.toDate ? gracePeriodEndsAt.toDate() : new Date(gracePeriodEndsAt);
    const now = new Date();
    const diffTime = endDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
}, [gracePeriodEndsAt]);

// Trial status calculations
const isTrialing = userData?.stripeSubscriptionStatus === 'trialing';
const trialActivated = userData?.trialActivated === true;
const trialEnd = userData?.trialEnd;

// Helper to safely convert Firestore Timestamp or Date
const getDateFromField = (field) => {
    if (!field) return null;
    if (typeof field.toDate === 'function') {
        return field.toDate();
    } else if (field instanceof Date) {
        return field;
    } else {
        return new Date(field);
    }
};

const trialDaysRemaining = useMemo(() => {
    const endDate = getDateFromField(trialEnd);
    if (!endDate) return null;
    const now = new Date();
    const diffTime = endDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
}, [trialEnd]);

const trialEndDateFormatted = useMemo(() => {
    const date = getDateFromField(trialEnd);
    if (!date) return null;
    return date.toLocaleDateString('en-US', { 
        month: 'long', 
        day: 'numeric', 
        year: 'numeric' 
    });
}, [trialEnd]);

// Check if user has any clients that need setup
  const clientsNeedingSetup = useMemo(() => {
    const validClients = Array.isArray(clients) ? clients : [];
    const setupNeeded = validClients.filter(c => c.isSetupComplete === false);
    console.log('🔍 DEBUG clientsNeedingSetup:', {
      totalClients: validClients.length,
      needingSetup: setupNeeded.length,
      setupClients: setupNeeded.map(c => ({ id: c.id, name: c.name }))
    });
    return setupNeeded;
  }, [clients]);
  const cardBrand = userData?.paymentMethodBrand || 'Card';
  const last4 = userData?.paymentMethodLast4 || '****';

  return (
    <Box>
      <Paper elevation={2} sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
        <Typography variant="h5" component="h1" gutterBottom>
          Account Management
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Review your subscription details, payment methods, and profile settings.
        </Typography>
      </Paper>

    {/* NEW: Persistent Success Message */}
    {completedClientName && (
        <Alert
            severity="success"
            sx={{ mb: 3 }}
            onClose={() => setCompletedClientName(null)} // Allows user to dismiss
        >
            <Typography variant="h6" component="div">
                🎉 Setup Activities Complete for "{completedClientName}"!
            </Typography>
            <Typography>
                The setup activities are now completed. You will be notified by email when the client is fully active.
            </Typography>
        </Alert>
    )}

    {/* Payment Failure Warning Banner */}
    {isPaymentPastDue && (
        <Alert
            severity="warning"
            icon={<WarningAmberIcon fontSize="large" />}
            sx={{ 
                mb: 3, 
                border: '2px solid #ed6c02',
                '& .MuiAlert-icon': {
                    alignItems: 'center'
                }
            }}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
                    ⚠️ Payment Required - Action Needed
                </Typography>
                <Typography variant="body1">
                    We were unable to process your most recent payment.
                    {paymentFailureReason && (
                        <> Reason: <em>{paymentFailureReason}</em></>
                    )}
                </Typography>
                <Typography variant="body1">
                    {gracePeriodDaysRemaining !== null && gracePeriodDaysRemaining > 0 ? (
                        <>
                            Your account remains fully active for the next <strong>{gracePeriodDaysRemaining} day{gracePeriodDaysRemaining !== 1 ? 's' : ''}</strong>.
                            {gracePeriodEndDate && (
                                <> After <strong>{gracePeriodEndDate}</strong>, access to your client data may be restricted.</>
                            )}
                        </>
                    ) : (
                        <strong>Your grace period has expired. Please update your payment method immediately to restore full access.</strong>
                    )}
                </Typography>
                <Box sx={{ mt: 1 }}>
                    <Button
                        variant="contained"
                        color="warning"
                        onClick={handleManageBilling}
                        disabled={isPortalLoading}
                        sx={{ 
                            fontWeight: 'bold',
                            position: 'relative'
                        }}
                    >
                        Update Payment Method Now
                        {isPortalLoading && (
                            <CircularProgress
                                size={24}
                                sx={{
                                    color: 'warning.light',
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    marginTop: '-12px',
                                    marginLeft: '-12px',
                                }}
                            />
                        )}
                    </Button>
                </Box>
            </Box>
        </Alert>
    )}

    {/* Trial Status Banner */}
    {isTrialing && !trialActivated && (
        <Alert
            severity="info"
            sx={{ 
                mb: 3, 
                border: '2px solid #0288d1',
                '& .MuiAlert-icon': {
                    alignItems: 'center'
                }
            }}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
                    🎉 You're on a Free Trial
                </Typography>
                <Typography variant="body1">
                    {trialDaysRemaining !== null ? (
                        <>
                            Your trial ends in <strong>{trialDaysRemaining} day{trialDaysRemaining !== 1 ? 's' : ''}</strong>
                            {trialEndDateFormatted && <> (on <strong>{trialEndDateFormatted}</strong>)</>}.
                        </>
                    ) : (
                        <>Your trial period is active.</>
                    )}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Ready to continue? Activate your subscription now to ensure uninterrupted access. You won't be charged until your trial ends.
                </Typography>
                <Box sx={{ mt: 1 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleActivateSubscription}
                        disabled={isActivating}
                        sx={{ 
                            fontWeight: 'bold',
                            position: 'relative'
                        }}
                    >
                        Activate Subscription
                        {isActivating && (
                            <CircularProgress
                                size={24}
                                sx={{
                                    color: 'primary.light',
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    marginTop: '-12px',
                                    marginLeft: '-12px',
                                }}
                            />
                        )}
                    </Button>
                </Box>
            </Box>
        </Alert>
    )}

    {/* Trial Activated Confirmation */}
    {isTrialing && trialActivated && (
        <Alert severity="success" sx={{ mb: 3 }}>
            <Typography variant="body1">
                ✅ <strong>Subscription Activated!</strong> Your trial continues until {trialEndDateFormatted || 'the trial end date'}. 
                Your first charge will occur automatically — no action needed.
            </Typography>
        </Alert>
    )}

    <Grid container spacing={3}>
        {/* Subscription Overview Section */}
        <Grid item xs={12} md={6}>
          <Paper elevation={1} sx={{ p: { xs: 2, md: 3 }, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Subscription Overview
            </Typography>
            <Divider sx={{ my: 2 }} />
            <InfoRow 
              label="Active Clients" 
              value={subscriptionDetails.activeClientsCount} 
            />
            <InfoRow 
              label="Cost Per Client" 
              value={`$${subscriptionDetails.costPerClient.toFixed(2)} / month`}
            />
            <Divider sx={{ my: 1 }} />
            <InfoRow 
              label="Estimated Total Monthly Bill" 
              value={`$${subscriptionDetails.totalMonthlyCost.toFixed(2)}`}
              isTotal={true}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              Your bill is calculated based on the number of active clients at the start of your billing cycle. Prorated charges may apply for clients added mid-cycle.
            </Typography>
          </Paper>
        </Grid>

        {/* Profile & Security Section */}
        <Grid item xs={12} md={6}>
          <Paper elevation={1} sx={{ p: { xs: 2, md: 3 }, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Profile & Security
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="body1" color="text.secondary">Login Email:</Typography>
              <Typography variant="body1">{user?.email}</Typography>
            </Box>
            <Box>
              <Button 
                variant="outlined" 
                onClick={handlePasswordReset}
              >
                Send Password Reset Email
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Clicking this will send a secure link to your email to change your password.
              </Typography>
              <Typography variant="body2" sx={{ mt: 2 }}>
                 <a 
                      href="https://www.savvai.ai/privacy-policy" 
                      target="_blank" 
                      rel="noopener noreferrer"
         >
        View Privacy Policy
    </a>
</Typography>
</Box>
</Paper>
</Grid>

{/* --- NEW 2FA COMPONENT --- */}
                <Grid item xs={12}>
                    <TwoFactorAuthCard 
                        user={user} 
                        onStatusChange={onClientDataChange} 
                    />
                </Grid>
                {/* --- END NEW 2FA COMPONENT --- */}

{/* Phase 3: Brand new user — open wizard directly */}
    {clients.length === 0 && !completedClientName && (
        <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: { xs: 2, md: 3 } }}>
                <Typography variant="h6" gutterBottom>
                    Setup Your First Client
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    Use the setup wizard to create your first client in minutes.
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => onOpenWizard(null)}
                    sx={{ textTransform: 'none' }}
                >
                    Start Client Setup
                </Button>
            </Paper>
        </Grid>
    )}

     {/* Phase 3: Clients needing setup — show card with "Continue Setup" button that opens wizard */}
 {clientsNeedingSetup.map(client => (
    <Grid item xs={12} key={client.id}>
        <Paper elevation={1} sx={{ p: { xs: 2, md: 3 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6">
                    Setup Required: {client.name || 'New Client'}
                </Typography>
                {clients.length > 1 && (
                    <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => handleCancelSetup(client.id)}
                    >
                        Cancel Setup
                    </Button>
                )}
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="body1" color="text.secondary">
                    Continue the setup wizard to activate this client.
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => onOpenWizard(client.id)}
                    sx={{ textTransform: 'none' }}
                >
                    Continue Setup
                </Button>
            </Box>
        </Paper>
    </Grid>
))}

    {/* Phase 3: "Create New Client" opens wizard instead of createBlankClient */}
{clientsNeedingSetup.length === 0 && (
    <Grid item xs={12}>
        <Paper elevation={1} sx={{ p: { xs: 2, md: 3 } }}>
            <Typography variant="h6" gutterBottom>
                Client Management
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="body1" color="text.secondary">
                    Add a new client to your portfolio to begin using our services for them.
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddCircleOutlineIcon />}
                    onClick={() => onOpenWizard(null)}
                    sx={{ textTransform: 'none' }}
                >
                    Create New Client
                </Button>
            </Box>
        </Paper>
    </Grid>
)}

 {/* Payment Method Section - Conditionally Rendered */}
{userData?.accountType === 'Paid' && (
    <Grid item xs={12}>
        <Paper elevation={1} sx={{ p: { xs: 2, md: 3 } }}>
            <Typography variant="h6" gutterBottom>
              Payment Method
            </Typography>
            <Divider sx={{ my: 2 }} />
            {hasPaymentMethod || isPaymentPastDue ? (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CreditCardIcon color={isPaymentPastDue ? "warning" : "primary"} sx={{ fontSize: 40 }}/>
            <Box>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {cardBrand} ending in {last4}
                    {isPaymentPastDue && (
                        <Typography component="span" color="warning.main" sx={{ ml: 1, fontWeight: 'bold' }}>
                            (Payment Failed)
                        </Typography>
                    )}
                </Typography>
                <Typography variant="body2" color={isPaymentPastDue ? "warning.main" : "text.secondary"}>
                    {isPaymentPastDue 
                        ? "Please update your payment method to continue service."
                        : "This will be used for all subscription charges."
                    }
                </Typography>
            </Box>
        </Box>
        <Button
    variant="outlined"
    onClick={handleManageBilling}
    disabled={isPortalLoading}
    sx={{ position: 'relative' }}
>
    Manage Billing
    {isPortalLoading && (
        <CircularProgress
            size={24}
            sx={{
                color: 'primary.light',
                position: 'absolute',
                top: '50%',
                left: '50%',
                marginTop: '-12px',
                marginLeft: '-12px',
            }}
        />
    )}
</Button>
    </Box>
) : (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="body1" color="text.secondary">
            No payment method on file.
        </Typography>
        <Button
            variant="contained"
            onClick={handleRedirectToCheckout}
            disabled={isRedirecting}
            sx={{ position: 'relative' }}
        >
            Add Card
            {isRedirecting && (
                <CircularProgress
                    size={24}
                    sx={{
                        color: 'primary.light',
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        marginTop: '-12px',
                        marginLeft: '-12px',
                    }}
                />
            )}
        </Button>
    </Box>
)}
          </Paper>
        </Grid>
        )}
     {/* Integrations Section */}
      <Grid item xs={12}>
  <Paper elevation={1} sx={{ p: { xs: 2, md: 3 } }}>
    <Typography variant="h6" gutterBottom>
      Integrations
    </Typography>
    <Divider sx={{ my: 2 }} />
    {isAsanaLoading ? (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={24} />
        <Typography sx={{ ml: 2 }}>Loading Asana Status...</Typography>
      </Box>
    ) : asanaConnection?.status === 'connected' ? (
      // --- CONNECTED STATE (Unchanged) ---
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CableIcon color="success" sx={{ fontSize: 40 }} />
          <Box>
            <Typography variant="body1" sx={{ fontWeight: 'medium' }}>Asana Connected</Typography>
            <Typography variant="body2" color="text.secondary">
              Connected as: {asanaConnection.asanaUserName || 'Unknown User'}
            </Typography>
          </Box>
        </Box>
        <Button variant="outlined" onClick={handleDisconnectAsana} disabled={isDisconnectingAsana} sx={{ position: 'relative' }}>
          Disconnect
          {isDisconnectingAsana && <CircularProgress size={24} sx={{ position: 'absolute' }} />}
        </Button>
      </Box>
    ) : asanaAuthUrl ? (
      // --- NEW: PROCEED TO ASANA STATE ---
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, p: 2, border: '1px dashed', borderColor: 'primary.main', borderRadius: 1 }}>
        <Typography variant="body1" sx={{ fontWeight: 'medium' }}>Ready to Connect</Typography>
        <Typography variant="body2" color="text.secondary" align="center">
          Click below to go to Asana's secure website to authorize the connection.
        </Typography>
        <Button component="a" href={asanaAuthUrl} variant="contained" size="large">
          Proceed to Asana
        </Button>
      </Box>
    ) : (
      // --- NOT CONNECTED STATE (Unchanged) ---
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CableIcon color="primary" sx={{ fontSize: 40 }} />
          <Box>
            <Typography variant="body1" sx={{ fontWeight: 'medium' }}>Asana</Typography>
            <Typography variant="body2" color="text.secondary">Connect your Asana account to create tasks from insights.</Typography>
          </Box>
        </Box>
        <Button variant="contained" onClick={handleConnectToAsana} disabled={isConnectingAsana} sx={{ position: 'relative' }}>
          Connect
          {isConnectingAsana && <CircularProgress size={24} sx={{ position: 'absolute' }} />}
        </Button>
      </Box>
    )}
  </Paper>
</Grid>
</Grid>
</Box>
);
}

export default AccountPage;

